import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import {
  clearLastSessionScope,
  clearTokens,
  getIdToken,
  getRefreshToken,
  SessionScope,
  setLastSessionScope,
  setTokens,
} from "src/api/tokenStorage";
import { RoleInDB, TokenWithUserAndKey } from "src/clients/taktile-api";
import { analytics } from "src/instrumentation/analytics";
import {
  loginUnscoped,
  loginWithRefreshToken,
  loginWithRoleScope,
  loginWithWorkspaceScope,
  RefreshRequestConfig,
} from "src/store/api";
import {
  decodeTokenRoles,
  decodeTokenScope,
  TokenRole,
} from "src/utils/decodeTokenScope";
import { resolveBootScope } from "src/utils/resolveBootScope";
import { isTaktileStaffEmail } from "src/utils/taktileStaff";

export type LocalStorageValue = string | undefined | null;

const isTransient = (error: unknown): boolean => {
  const status = (error as { response?: { status?: number } }).response?.status;
  return (
    status === undefined || status >= 500 || status === 408 || status === 429
  );
};

type Refresh = (
  options?: RefreshRequestConfig,
  scope?: SessionScope,
) => Promise<TokenWithUserAndKey | void>;

// True on minted tokens; false on any failure or an undefined result (401
// recovery cleared the session mid-flight).
const attempt = async (refresh: Refresh, scope?: SessionScope) => {
  try {
    return (await refresh(undefined, scope)) !== undefined;
  } catch {
    return false;
  }
};

// Scoped mint, one same-scope retry on transient failures, unscoped fallback;
// rethrows the original error when nothing minted.
const bootRefresh = async (refresh: Refresh): Promise<void> => {
  const scope = resolveBootScope();
  try {
    await refresh(undefined, scope);
  } catch (error) {
    if (!scope) {
      throw error;
    }
    if (isTransient(error) && (await attempt(refresh, scope))) {
      return;
    }
    if (!(await attempt(refresh))) {
      throw error;
    }
  }
};

export const useAuthStore = create<
  ZustandStore<
    {
      refreshed: boolean;
      signed_in_user_id: string | undefined;
      signed_in_username: string | undefined;
      signed_in_avatar_url: string | undefined;
      signed_in_email: string | undefined;
      id_token: LocalStorageValue;
      refresh_token: LocalStorageValue;
      api_key: string | undefined;
      signed_in_full_name: string | undefined;
      hashed_email: string | undefined;
      roles: TokenRole[] | undefined;
      scope_org_id: string | undefined;
      scope_ws_id: string | undefined;
      scope_flow_folder_ids: string[] | undefined;
      scope_roles: string[] | undefined;
      loggedOut: boolean;
    },
    {
      resetAll: () => void;
      setup: () => void;
      refresh: (
        options?: RefreshRequestConfig,
        scope?: SessionScope,
      ) => Promise<TokenWithUserAndKey | void>;
      setRoleScope: (
        roles: string[],
        flowFolderIds?: string[],
      ) => Promise<void>;
      setSessionScope: (
        orgId: string,
        wsId: string,
        options?: { signal?: AbortSignal },
      ) => Promise<void>;
      clearSessionScope: (options?: { signal?: AbortSignal }) => Promise<void>;
      setCredentials: (
        signed_in_user_id: string,
        signed_in_username: string,
        signed_in_full_name: string | undefined,
        signed_in_email: string,
        signed_in_avatar_url: string | undefined,
        id_token: string,
        refresh_token: string,
        api_key: string,
        hashed_email?: string,
        roles?: RoleInDB[] | undefined,
      ) => void;
    }
  >
>((set, get) => ({
  refreshed: false,
  signed_in_username: undefined,
  signed_in_user_id: undefined,
  signed_in_avatar_url: undefined,
  signed_in_full_name: undefined,
  api_key: undefined,
  signed_in_email: undefined,

  id_token: getIdToken(),
  refresh_token: getRefreshToken(),
  hashed_email: undefined,
  roles: undefined,
  scope_org_id: decodeTokenScope(getRefreshToken()).orgId,
  scope_ws_id: decodeTokenScope(getRefreshToken()).wsId,
  scope_flow_folder_ids: decodeTokenScope(getRefreshToken()).flowFolderIds,
  scope_roles: decodeTokenScope(getRefreshToken()).roles,
  loggedOut: false,

  actions: {
    setup: async () => {
      set({ refreshed: false });
      await bootRefresh(get().actions.refresh);
      set({ refreshed: true });
    },
    setSessionScope: async (orgId, wsId, options) => {
      // Folder ids are kept unless we switch to a different workspace: ids
      // picked inside a workspace are local to it, while ids on an unscoped
      // token are a token-wide narrowing from the admin panel that dropping
      // would silently widen.
      const previousWsId = get().scope_ws_id;
      const flowFolderIds =
        previousWsId === undefined || wsId === previousWsId
          ? get().scope_flow_folder_ids
          : undefined;
      const { user, id_token, refresh_token, api_key } =
        await loginWithWorkspaceScope(
          orgId,
          wsId,
          get().scope_roles,
          flowFolderIds,
          options,
        );
      // Don't apply a mint superseded by a newer workspace switch (request
      // resolved just as its query was cancelled). Bail silently rather than
      // throwing, so no error state / boundary is hit.
      if (options?.signal?.aborted) {
        return;
      }
      get().actions.setCredentials(
        user.id,
        user.username ?? "",
        user.full_name ?? undefined,
        user.email ?? "",
        user.avatar_url ?? undefined,
        id_token,
        refresh_token,
        api_key?.api_key ?? "",
        user.hashed_email,
        user.roles,
      );
    },
    setRoleScope: async (roles, flowFolderIds) => {
      const { scope_org_id, scope_ws_id } = get();
      const { user, id_token, refresh_token, api_key } =
        await loginWithRoleScope(
          roles,
          scope_org_id,
          scope_ws_id,
          flowFolderIds,
        );
      get().actions.setCredentials(
        user.id,
        user.username ?? "",
        user.full_name ?? undefined,
        user.email ?? "",
        user.avatar_url ?? undefined,
        id_token,
        refresh_token,
        api_key?.api_key ?? "",
        user.hashed_email,
        user.roles,
      );
    },
    clearSessionScope: async (options) => {
      // The backend applies flow_folder_ids to every folder-dimensioned role
      // in the token, so an unscoped mint must not inherit them: stale ids
      // would silently disable those roles in every other workspace.
      const { user, id_token, refresh_token, api_key } = await loginUnscoped(
        get().scope_roles,
        undefined,
        options,
      );
      if (options?.signal?.aborted) {
        return;
      }
      get().actions.setCredentials(
        user.id,
        user.username ?? "",
        user.full_name ?? undefined,
        user.email ?? "",
        user.avatar_url ?? undefined,
        id_token,
        refresh_token,
        api_key?.api_key ?? "",
        user.hashed_email,
        user.roles,
      );
    },
    refresh: async (options, scope) => {
      if (get().refresh_token) {
        const response = await loginWithRefreshToken(options, scope);
        get().actions.setCredentials(
          response.user.id,
          response.user.username ?? "",
          response.user.full_name ?? undefined,
          response.user.email ?? "",
          response.user.avatar_url ?? undefined,
          response.id_token,
          response.refresh_token,
          response.api_key.api_key,
          response.user.hashed_email,
          response.user.roles,
        );
        return response;
      }
    },

    setCredentials: (
      signed_in_user_id: string,
      signed_in_username: string,
      signed_in_full_name: string | undefined,
      signed_in_email: string,
      signed_in_avatar_url: string | undefined,
      id_token: string,
      refresh_token: string,
      api_key: string,
      hashed_email?: string,
      roles?: RoleInDB[] | undefined,
    ) => {
      if (get().loggedOut) {
        return;
      }
      const scope = decodeTokenScope(refresh_token);
      if (scope.orgId) {
        setLastSessionScope({ orgId: scope.orgId, wsId: scope.wsId });
      } else {
        // deliberately unscoped (e.g. admin panel): forget the old scope so
        // the next boot doesn't re-mint it just to undo it
        clearLastSessionScope();
      }
      const sessionRoles = scope.roles
        ? (decodeTokenRoles(id_token) ?? roles)
        : roles;
      set({
        id_token,
        refresh_token,
        signed_in_user_id,
        signed_in_username,
        signed_in_full_name,
        signed_in_email,
        signed_in_avatar_url,
        api_key,
        hashed_email,
        roles: sessionRoles,
        scope_org_id: scope.orgId,
        scope_ws_id: scope.wsId,
        scope_flow_folder_ids: scope.flowFolderIds,
        scope_roles: scope.roles,
      });
      setTokens(id_token, refresh_token);

      analytics.identify(signed_in_user_id, {
        username: signed_in_username,
        $set_once: { username: signed_in_username },
      });
    },

    resetAll: () => {
      clearTokens();

      set({
        loggedOut: true,
        refreshed: undefined,
        signed_in_user_id: undefined,
        signed_in_username: undefined,
        signed_in_avatar_url: undefined,
        id_token: undefined,
        refresh_token: undefined,
        api_key: undefined,
        signed_in_full_name: undefined,
        hashed_email: undefined,
        roles: undefined,
        scope_org_id: undefined,
        scope_ws_id: undefined,
        scope_flow_folder_ids: undefined,
        scope_roles: undefined,
      });
    },
  },
}));

export const useIsUserRefreshed = () =>
  useAuthStore((state) => state.refreshed);

export const useCurrentUserId = () =>
  useAuthStore((state) => state.signed_in_user_id);

export const useTokenRoleScope = () =>
  useAuthStore((state) => state.scope_roles);

export const useTokenFlowFolderScope = () =>
  useAuthStore((state) => state.scope_flow_folder_ids);

export const useIsTaktileStaffEmail = () =>
  useAuthStore((state) => isTaktileStaffEmail(state.signed_in_email));

export const useUserData = () =>
  useAuthStore(
    useShallow((state) => ({
      signed_in_full_name: state.signed_in_full_name,
      signed_in_username: state.signed_in_username,
      signed_in_avatar_url: state.signed_in_avatar_url,
      signed_in_user_id: state.signed_in_user_id,
      signed_in_email: state.signed_in_email,

      api_key: state.api_key,
      hashed_email: state.hashed_email,

      roles: state.roles,
    })),
  );

export const useAuthActions = () => useAuthStore((state) => state.actions);

/**
 * The current session scope. Use it to namespace React Query keys for
 * endpoints that return scope-dependent data (e.g. GET /organizations,
 * GET /workspaces), so switching scope refetches instead of serving the
 * previous scope's cached response.
 */
export const useSessionScope = () =>
  useAuthStore(
    useShallow((state) => ({
      orgId: state.scope_org_id,
      wsId: state.scope_ws_id,
    })),
  );

export const useIsScopedTo = (
  orgId: string | undefined,
  wsId: string | undefined,
) =>
  useAuthStore(
    (state) =>
      !orgId ||
      !wsId ||
      (state.scope_org_id === orgId && state.scope_ws_id === wsId),
  );

export const useIsFWDOrAdmin = () =>
  useAuthStore(
    (state) =>
      state.roles?.some((r) =>
        ["taktile_d_admin", "fwd_d", "oncall_write"].includes(r.name),
      ) ?? false,
  );

export const useIsEngineerOrAdmin = () =>
  useAuthStore(
    (state) =>
      state.roles?.some((r) =>
        [
          "taktile_d_admin",
          "taktile_admin",
          "oncall_group",
          "oncall_write",
        ].includes(r.name),
      ) ?? false,
  );

export const useHasFwdRoleForOrg = (orgId: string | undefined) =>
  useAuthStore(
    (state) =>
      !!orgId &&
      (state.roles?.some(
        (r) => r.name === "fwd_d" && r.arguments?.org_id === orgId,
      ) ??
        false),
  );

export const useIsTaktileAdmin = () =>
  useAuthStore(
    (state) =>
      state.roles?.some(
        // Oncall_write inherits from taktile_d_admin
        (r) => r.name === "taktile_d_admin" || r.name === "oncall_write",
      ) ?? false,
  );

const ADMIN_READ_ROLE_NAMES = [
  "taktile_admin",
  "taktile_d_admin",
  "oncall_write",
  "oncall_group",
];

/**
 * Whether the user's roles grant the platform-wide reads the admin panel's
 * global endpoints validate (`r:organization/*`, `r:internal_resources/*`).
 */
export const useCanReadAdminResources = () =>
  useAuthStore(
    (state) =>
      state.roles?.some((role) => ADMIN_READ_ROLE_NAMES.includes(role.name)) ??
      false,
  );

export const useUserRoles = () => useAuthStore((state) => state.roles);

export const useHasRole = (roleName: string) =>
  useAuthStore(
    (state) => state.roles?.some((r) => r.name === roleName) ?? false,
  );
